import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { filterParamsKey } from "./usePagedList";

/**
 * One read per filter set: `GET path?<filters>`, for a figure that belongs to the filters and
 * not to a page (a footer totalled over every page). It is asked again when the filters
 * change or on `retry()`, never on a page turn, which is the point: such a read can cost far
 * more than a page.
 *
 * - `filters` follow `usePagedList`'s blank rule (`""`, null, undefined are not sent).
 * - `isPayload(data)` says whether a 200 is the answer at all (the production proxy answers
 *   200 + index.html for an unknown /api path); anything else is a failed read. Pass a
 *   module-level function: it is an effect dependency.
 * - Only the newest request may land (the effect cleanup is the ticket).
 *
 * Returns `{ data, error, loading, retry }`, all about the filters in force NOW: `data` is
 * their answer (null while they load, or after a failure: another filter set's answer is
 * never shown under these), `error` what their newest read failed with, `loading` true
 * until that read answers.
 */
export function useFilteredRead({ path, filters, isPayload }) {
  // The newest answer and the filter key it belongs to; `pending` while a read is out.
  const [state, setState] = useState({ key: null, data: null, error: null, pending: true });
  const [request, setRequest] = useState(0);
  const filterKey = filterParamsKey(filters);

  useEffect(() => {
    let cancelled = false;
    setState((cur) => ({ ...cur, pending: true }));
    api.get(path, { params: JSON.parse(filterKey) })
      .then((r) => {
        if (!isPayload(r.data)) throw new Error(`${path} payload`);
        if (!cancelled) setState({ key: filterKey, data: r.data, error: null, pending: false });
      })
      .catch((error) => {
        if (!cancelled) setState({ key: filterKey, data: null, error, pending: false });
      });
    return () => { cancelled = true; };
  }, [path, filterKey, request, isPayload]);

  const retry = useCallback(() => setRequest((n) => n + 1), []);
  const current = state.key === filterKey && !state.pending;
  return {
    data: current ? state.data : null,
    error: current ? state.error : null,
    loading: !current,
    retry,
  };
}
